import React, { useMemo, useRef } from 'react';

const fmtBps = (bps) => {
  if (!bps || bps < 1) return '0 B/s';
  const u = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
  const i = Math.min(u.length - 1, Math.floor(Math.log(bps) / Math.log(1024)));
  return `${(bps / 1024 ** i).toFixed(i >= 2 ? 1 : 0)} ${u[i]}`;
};
const fmtMbps = (m) => (m == null ? '—' : `${m >= 100 ? Math.round(m) : m.toFixed(1)} Mbps`);
const fmtMs = (m) => (m == null ? '—' : `${Math.round(m)} ms`);

// SVG area chart of upload throughput over time.
function ThroughputChart({ samples, peakBps, liveBps }) {
  const W = 560;
  const H = 96;
  const path = useMemo(() => {
    if (!samples || samples.length < 2) return null;
    const max = Math.max(peakBps || 0, ...samples.map((s) => s.bps), 1);
    const n = samples.length;
    const x = (i) => (i / (n - 1)) * W;
    const y = (b) => H - 4 - (b / max) * (H - 12);
    const line = samples.map((s, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(s.bps).toFixed(1)}`).join(' ');
    const area = `${line} L${W},${H} L0,${H} Z`;
    return { line, area, max };
  }, [samples, peakBps]);

  return (
    <div className="cs-us-chart">
      <div className="cs-us-chart-head">
        <span>Upload throughput</span>
        <strong>{fmtBps(liveBps)}</strong>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="cs-us-chart-svg">
        <defs>
          <linearGradient id="csUsFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(45,212,191,0.45)" />
            <stop offset="100%" stopColor="rgba(45,212,191,0.02)" />
          </linearGradient>
        </defs>
        {path ? (
          <>
            <path d={path.area} fill="url(#csUsFill)" />
            <path d={path.line} fill="none" stroke="#2dd4bf" strokeWidth="2" vectorEffect="non-scaling-stroke" />
          </>
        ) : (
          <line x1="0" y1={H - 4} x2={W} y2={H - 4} stroke="#2a2a33" strokeWidth="1" />
        )}
      </svg>
      <div className="cs-us-chart-foot">
        <span>peak {fmtBps(peakBps)}</span>
        <span>last ~90s</span>
      </div>
    </div>
  );
}

// "Packet" grid for the active file: one cell per 100MB part.
function PacketGrid({ parts, partCount, activeName, doneParts }) {
  if (!partCount) return null;
  // Keep the DOM light for very large files (e.g. 50GB → 512 parts).
  const cells = parts.length > 320 ? parts.filter((_, i) => i % Math.ceil(parts.length / 320) === 0) : parts;
  return (
    <div className="cs-us-packets">
      <div className="cs-us-chart-head">
        <span title={activeName}>
          Parts · <span className="cs-us-dim">{activeName || 'current file'}</span>
        </span>
        <strong>
          {doneParts}/{partCount}
        </strong>
      </div>
      <div className="cs-us-grid">
        {cells.map((p) => (
          <span
            key={p.n}
            className={`cs-us-cell st-${p.state}`}
            style={p.state === 'active' && p.size ? { '--fill': `${Math.round((p.loaded / p.size) * 100)}%` } : undefined}
            title={`Part ${p.n} · ${p.state}`}
          />
        ))}
      </div>
      <div className="cs-us-legend">
        <span><i className="st-done" /> done</span>
        <span><i className="st-active" /> sending</span>
        <span><i className="st-retry" /> retry</span>
        <span><i className="st-queued" /> queued</span>
      </div>
    </div>
  );
}

export default function UploadStats({ snapshot, conn, testing, onTest, online, liveBps }) {
  const eff = snapshot ? Math.round(snapshot.efficiency * 100) : 100;
  // Upload speed is the REAL transfer rate (bytes actually leaving the machine),
  // not a synthetic probe — so it stays consistent with the throughput chart and
  // never shows a buffer-inflated number. Remember the last live rate so the
  // figure persists after a file finishes instead of dropping back to "—".
  const liveUpMbps = liveBps > 0 ? (liveBps * 8) / 1e6 : null;
  const lastUpRef = useRef(null);
  if (liveUpMbps != null) lastUpRef.current = liveUpMbps;
  const upMbps = liveUpMbps != null ? liveUpMbps : lastUpRef.current;
  return (
    <div className="cs-us">
      <div className="cs-us-row">
        <ThroughputChart samples={snapshot?.samples} peakBps={snapshot?.peakBps} liveBps={liveBps} />
        <div className="cs-us-conn">
          <div className="cs-us-chart-head">
            <span>Connection</span>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onTest} disabled={testing || online === false}>
              {testing ? 'Testing…' : 'Run speed test'}
            </button>
          </div>
          <div className="cs-us-conn-grid">
            <div className="cs-us-stat">
              <span>Ping</span>
              <strong>{fmtMs(conn?.pingMs)}</strong>
            </div>
            <div className="cs-us-stat">
              <span>Download</span>
              <strong>{fmtMbps(conn?.downMbps ?? conn?.downlinkMbps)}</strong>
            </div>
            <div className="cs-us-stat">
              <span>Upload</span>
              <strong>{fmtMbps(upMbps)}</strong>
            </div>
            <div className="cs-us-stat">
              <span>Network</span>
              <strong>{conn?.effectiveType ? conn.effectiveType.toUpperCase() : online === false ? 'OFFLINE' : '—'}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="cs-us-row">
        <PacketGrid
          parts={snapshot?.parts || []}
          partCount={snapshot?.partCount || 0}
          activeName={snapshot?.activeName}
          doneParts={snapshot?.doneParts || 0}
        />
        <div className="cs-us-eff">
          <div className="cs-us-chart-head">
            <span>Transfer efficiency</span>
            <strong className={eff < 95 ? 'warn' : ''}>{eff}%</strong>
          </div>
          <div className="cs-us-conn-grid">
            <div className="cs-us-stat">
              <span>Retries</span>
              <strong>{snapshot?.retries || 0}</strong>
            </div>
            <div className="cs-us-stat">
              <span>Re-sent</span>
              <strong>{snapshot ? `${(snapshot.retriedBytes / 1024 / 1024).toFixed(0)} MB` : '0 MB'}</strong>
            </div>
            <div className="cs-us-stat">
              <span>Sending now</span>
              <strong>{snapshot?.activeParts || 0} parts</strong>
            </div>
            <div className="cs-us-stat">
              <span>Status</span>
              <strong>{online === false ? 'paused' : (snapshot?.activeParts || 0) > 0 ? 'live' : 'idle'}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
